import { memo } from 'react';
import { COLORS, LABELS } from '@/game/configs';
import { type Difficulty } from '@/game/configs';
import { Clock, AlertCircle, Pause, Play } from 'lucide-react';
interface GameHeaderProps {
  difficulty: Difficulty;
  timerSeconds: number;
  mistakes: number;
  isPaused: boolean;
  onPause: () => void;
}
export const GameHeader = memo(function GameHeader({
  difficulty,
  timerSeconds,
  mistakes,
  isPaused,
  onPause,
}: GameHeaderProps) {
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const difficultyLabels: Record<Difficulty, string> = {
    easy: LABELS.easy,
    medium: LABELS.medium,
    hard: LABELS.hard,
  };
  return (
    <div
      className="flex items-center justify-between w-full max-w-[400px] mx-auto px-2 py-3 rounded-xl"
      style={{ backgroundColor: COLORS.cream, boxShadow: '4px 4px 8px rgba(139, 115, 85, 0.15), -4px -4px 8px rgba(255, 255, 255, 0.9)' }}
    >
      <div className="flex flex-col items-start">
        <span
          className="text-sm font-bold"
          style={{ color: COLORS.brown }}
        >
          {difficultyLabels[difficulty]}
        </span>
      </div>
      <div className="flex items-center gap-4">
        <div
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
          style={{ backgroundColor: COLORS.ivory, boxShadow: 'inset 2px 2px 4px rgba(139, 115, 85, 0.1), inset -2px -2px 4px rgba(255, 255, 255, 0.8)' }}
        >
          <Clock size={16} style={{ color: COLORS.brown }} />
          <span
            className="text-sm font-bold tabular-nums"
            style={{ color: COLORS.dark }}
          >
            {formatTime(timerSeconds)}
          </span>
        </div>
        <div
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all duration-300 ${
            mistakes > 0 ? 'animate-pulse' : ''
          }`}
          style={{ backgroundColor: mistakes > 0 ? '#FEE2E2' : COLORS.ivory, boxShadow: 'inset 2px 2px 4px rgba(139, 115, 85, 0.1), inset -2px -2px 4px rgba(255, 255, 255, 0.8)' }}
        >
          <AlertCircle
            size={16}
            style={{ color: mistakes > 0 ? '#DC2626' : COLORS.brown }}
          />
          <span
            className="text-sm font-bold tabular-nums"
            style={{ color: mistakes > 0 ? '#DC2626' : COLORS.dark }}
          >
            {mistakes}/3
          </span>
        </div>
        <button
          onClick={onPause}
          className="flex items-center justify-center w-12 h-12 rounded-full transition-all duration-150 active:scale-95"
          style={{
            backgroundColor: COLORS.cream,
            boxShadow: '4px 4px 8px rgba(139, 115, 85, 0.2), -4px -4px 8px rgba(255, 255, 255, 0.9)',
          }}
          aria-label={isPaused ? 'Resume' : 'Pause'}
        >
          {isPaused ? (
            <Play size={20} style={{ color: COLORS.brown }} />
          ) : (
            <Pause size={20} style={{ color: COLORS.brown }} />
          )}
        </button>
      </div>
    </div>
  );
});